import "@/main.scss";
import "vuetify/styles";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import * as labs from "vuetify/labs/components";
import { h } from "vue";
import type { IconSet, IconProps, ThemeDefinition } from "vuetify";
import { paginationOptions } from "@/constants/table";
import { i18n } from "@/plugins/i18n";
import { createVueI18nAdapter } from "vuetify/locale/adapters/vue-i18n";
import { useI18n } from "vue-i18n";

const custom: IconSet = {
  component: (props: IconProps) => h(props.tag, { class: ["icon", props.icon] }),
};

const aliases: { [key: string]: string } = {
  chevron: "icon-chevron-down-xs",
  blank: "icon-hex-empty-l",
  bell: "icon-notifications-xs",
  groups: "icon-users-s",
  search: "icon-search-xs",
  devices: "icon-devices-xl",
  email: "icon-email-s",
  calendar: "icon-calendar-s",
  user: "icon-user-s",
  settings: "icon-settings-xs",
  expand: "icon-chevron-right-xs",
  minimize: "icon-chevron-left-xs",
  cloud: "icon-cloud-app-s",
  check: "icon-check-xs",
  x: "icon-close-xs",
  dots: "icon-action-menu-vertical-xs",
  close: "icon-close-xs",
  chevronDown: "icon-chevron-down-xs",
  checkboxMarked: "icon-checkbox-marked",
  checkboxBlankOutline: "icon-checkbox-blank-outline",
  minus: "icon-minus-box",
  radio: "icon-radiobox-blank",
  radioMarked: "icon-radiobox-marked",
  closeCircle: "icon-rules-close",
  trash: "icon-deleted-xs",
  export: "icon-export2-xs",
  file: "icon-data-no-hex-l",
  info: "icon-event-detail-s",
  infoXs: "icon-info-xs",
  lock: "icon-password-s",
  copy: "icon-copy-xs",
  smartphone: "icon-mobile-device-s",
  desktop: "icon-device-s",
  apple: "icon-apple-xs",
  android: "icon-android-logo-s",
  windows: "icon-windows-logo-s",
  users: "icon-users-xl",
  cloudApps: "icon-cloud-apps-xl",
  locationAccess: "icon-vpn-l",
  add: "icon-add-xs",
  warning: "icon-warning-xs",
  circleCheck: "icon-success-xs",
  announcement: "icon-announcement",
  eye: "icon-visibility-xs",
  eyeOff: "icon-visibility-off-xs",
  abnormalAdminActivity: "icon-abnormal-admin-activity-s",
  abnormalDataManipulation: "icon-abnormal-data-manipulation-s",
  accessRestrictions: "icon-global-xl",
  accessViolation: "icon-access-restriction-violation-s",
  accountSecurity: "icon-account-security-l",
  activityLogs: "icon-activity-logs-l",
  adminUsers: "icon-admin-users-l",
  dlpEmail: "icon-data-loss-email-s",
  dlpFile: "icon-data-loss-file-s",
  dlp: "icon-dlp-exposed",
  emailPhishing: "icon-email-phishing-s",
  endpointVulnerability: "icon-endpoint-vulnerability-s",
  identityCompromise: "icon-suspected-identity-compromise-s",
  listAuthentication: "icon-password-s",
  listMalware: "icon-malware-no-hex-l",
  malwareEmail: "icon-malware-in-attachment-s",
  malwareEndpoint: "icon-malware-in-endpoint-s",
  malwareInCloud: "icon-malware-in-cloud-s",
  malware: "icon-malware-no-hex-l",
  multipleFailedLogin: "icon-multiple-failed-logins-s",
  phishing: "icon-phishing-no-hex-l",
  resetPassword: "icon-password-reset-xl",
  undone: "icon-activity-undone-s",
  wifiPhishing: "icon-wifi-phishing-s",
  emails: "icon-email-xl",
  data: "icon-data-xl",
  laptop: "icon-device-s",
  dataLoss: "icon-data-loss-l",
  noAlerts: "icon-no-alerts-l",
  noIssues: "icon-success-xs",
  triangle: "icon-arrow-down-xs",
  graphSquared: "icon-detection-log-xs",
  billing: "icon-billing-l",
  coroFlexible: "icon-subscriptions-l",
  usersSaySafe: "icon-email-safe-xs",
  usersSayPhishing: "icon-email-phishing-xs",
  manageWorkspaces: "icon-manage-workspaces-xs",
  channelWorkspace: "icon-parent-plan-s",
  childWorkspace: "icon-child-plan-s",
  regularWorkspace: "icon-regular-plan-s",
  noProtection: "icon-no-protection-xs",
  allGood: "icon-no-tickets-l",
  massDownload: "icon-mass-downloads-s",
  massDelete: "icon-mass-deletion-s",
  vulnerability: "icon-vulnerability",
  developmentMode: "icon-development-mode-enabled-s",
  nonGenuineWindows: "icon-none-genuine-windows-s",
  deviceEncryption: "icon-device-encryption-s",
  windowsHexagon: "icon-windows-mass-deployment-xl",
  sync: "icon-sync-xs",
  macOSHexagon: "icon-mac-mass-deployment-xl",
  process: "icon-edr-s",
  hardDrive: "icon-hard-drive",
  fileDevice: "icon-device-file-s",
  help: "icon-help-xs",
  connectors: "icon-connectors-l",
  globe: "icon-everywhere-s",
  globeBlocked: "icon-url-blocked-s",
  ipAddress: "icon-ip2-s",
  geoCountry: "icon-flag-xs",
  tld: "icon-tld-s",
  pin: "icon-pin-s",
  wifiNew: "icon-wifi-xs",
  wifiForbidden: "icon-wifi-no-entry-s",
  redAlert: "icon-issue-s",
  uploadFile: "icon-upload-s",
  coroMail: "icon-coromail-l",
  firewallDisabled: "icon-firewall-disabled-s",
  addonNetwork: "icon-network-l",
  trashOutlined: "icon-deleted-xs",
  fileEmpty: "icon-file-s",
  folder: "icon-folder-s",
  endpointDataGovernance: "icon-endpoint-data-governance-l",
  edrHexagon: "icon-edr-l",
  registryKey: "icon-registry-key-s",
  scheduledTask: "icon-schedueled-task-s",
  accountEvent: "icon-windows-event-log",
  mdm: "icon-mobile-device-management-l",
  folderLocked: "icon-folder-coro-s",
  usersNoHexagon: "icon-user-no-hex-l",
  devicesNoHexagon: "icon-devices-no-hex-l",
  cloudAppsNoHexagon: "icon-cloud-no-hex-l",
  macPc: "icon-mac-device-s",
  windowsPc: "icon-windows-device-s",
  linuxPc: "icon-linux-device-s",
  iosDevice: "icon-ios-device-s",
  androidDevice: "icon-android-device-s",
  socSettings: "icon-soc-l",
  comments: "icon-comments-l",
  dropdown: "icon-chevron-down-xs",
  dataNoHexagon: "icon-data-no-hex-l",
  success: "icon-success-xs",
  alertTriangle: "icon-alert-triangle-l",
  firstPage: "icon-first-page-xs",
  lastPage: "icon-last-page-xs",
  chevronRight: "icon-chevron-right-xs",
  chevronLeft: "icon-chevron-left-xs",
  sortOption: "icon-filter2-xs",
  sortArrow: "icon-sort-arrow-up",
  sortArrowDown: "icon-sort-arrow-down",
  sortAsc: "icon-sort-arrow-up",
  sortDesc: "icon-sort-arrow-down",
  zipFile: "icon-data-file-sharing-zip-s",
  csvFile: "icon-data-file-sharing-csv-s",
  trailingLink: "icon-link-xs",
  secureMessagesSmall: "icon-secure-messages-2-s",
  dns: "icon-network-s",
  soc: "icon-soc-no-hex-l",
  sigmaMatch: "icon-device-file-s",
  inboundGatewayLarge: "icon-security-l",
  ztnaLarge: "icon-malware-l",
  wifiPhishingLarge: "icon-phishing-l",
  dnsReport: "icon-dns-l",
  emailWarning: "icon-email-warning-l",
  recentSearch: "icon-recent-search",
  fullDetailsL: "icon-full-details-l",
  fileUploadL: "icon-file-upload-l",
  forward: "icon-forward-xs",
  confidential: "icon-lock-xs",
  dashboard: "icon-actionboard-xs",
  coroConsole: "icon-app-coro-console-theme-dark",
  inactiveUser: "icon-inactive-user",
  emptyCalendar: "icon-schedule-xs",
  splitTunneling: "icon-vpn-split-tunnel-2-l",
  fullTunneling: "icon-vpn-full-tunnel-2-l",
  defaultVpn: "icon-default-vpn-l",
  includedResources: "icon-included-in-vpn-s",
  thirdPartyApp: "icon-third-party-apps-s",
  securityTraining: "icon-security-awareness-training-2-s",
  securityAwareness: "icon-security-awareness-training-l",
  securityAwarenessNoHex: "icon-security-awareness-training-no-hex-l",
  adaptiveTraining: "icon-adaptive-training-l",
  label: "icon-label-l",
  labelXs: "icon-label-xs",
  externalLink: "icon-external-link-xs",
  playXs: "icon-play-xs",
  iconDevicesWithMobileNoHex: "icon-devices-with-mobile-no-hex-2-l",
  endpointDataGovernanceXs: "icon-endpoint-data-governance-xs",
  edrXs: "icon-edr-xs",
  userDataGovernanceXs: "icon-user-data-governance-xs",
  userXs: "icon-user-xs",
  mailUnreadXs: "icon-mail-unread-xs",
  endpointXs: "icon-endpoint-xs",
  cloudSecurityXs: "icon-cloud-security-xs",
  settingsXs: "icon-settings-xs",
  settingsXs2: "icon-settings-xs-2",
  activityLogsXs: "icon-activity-log-xs",
  lockXs: "icon-lock-xs",
  reportsSmall: "icon-reports-s",
  linuxHexagon: "icon-linux-hexagon",
  proxySettings: "icon-proxy-setttings-l",
  emailNoHex: "icon-email-no-hex-l",
  retractXs: "icon-mail-retract-xs",
  userDataSm: "icon-data-s",
  mdmSm: "icon-mobile-device-cob-s",
  noTicketsL: "icon-no-tickets-l",
  globeLocation: "icon-ip3-s",
  wifiTrusted: "icon-wifi-trusted-s",
  abnormalTime: "icon-abnormal-time-s",
  impossibleTraveler: "icon-impossible-travel-s",
  outboundGateway: "icon-outbound-s",
  inboundGateway: "icon-included-in-vpn-s",
  mspLabels: "icon-placeholder-s",
  userData: "icon-user-data-governance-s",
  newMessage: "icon-new-chat-xs",
  aiSummary: "icon-template-xs",
  ask: "icon-onboarding-helper-xs",
  like: "icon-thumbs-up-xs",
  dislike: "icon-thumbs-down-xs",
  inputFrame: "icon-input-frame",
  helpLarge: "icon-help-l",
  aiAssistant: "icon-ai-assistant-xs",
  link: "icon-link-xs",
  refresh: "icon-sync-xs",
  partnersLarge: "icon-partners-l",
  successSmall: "icon-success-s",
  return: "icon-return",
  ticketsXs: "icon-tickets-xs",
  allowBlockListsLarge: "icon-allow-block-lists-l",
  securityGaps: "icon-security-gaps-s",
  quickFix: "icon-insights-xs",
  protectionXs: "icon-protection-xs",

  autoResolveS: "icon-auto-resolve-2-s",
  cloudAppS: "icon-cloud-app-s",
  dataS: "icon-data-s",
  deviceS: "icon-device-s",
  edrS: "icon-edr-s",
  emailS: "icon-email-s",
  endpointDataGovernanceS: "icon-endpoint-data-governance-s",
  insightsXs: "icon-insights-xs",
  issueS: "icon-issue-s",
  networkS: "icon-network-s",
  protectionIssueXs: "icon-protection-issue-xs",
  secureMessagesS: "icon-secure-messages-2-s",
  securityAwarenessTrainingS: "icon-security-awerness-training-s",
  usersS: "icon-users-s",
  wifiPhishingS: "icon-wifi-phishing-s",
  dnsAlternativeS: "icon-dns2-s",
  ztnaS: "icon-ztna-s",
  cube: "icon-child-plan-s",
  tickets3Xs: "icon-tickets3-xs",
  chevronUp: "icon-chevron-up-xs",
  inboundGatewayDeemed: "icon-proxy-settings-l",
  pushPinXs: "icon-pushpin-xs",
  suspectedIdentityCompromiseS: "icon-suspected-identity-compromise-s",
  arrowDownXs: "icon-arrow-down-xs",
  arrowRightXs: "icon-arrow-right-xs",
  bundleXs: "icon-bundle-xs",
  channelXs: "icon-channel-xs",
  collapseAllXs: "icon-collapse-all-xs",
  expandAllXs: "icon-expand-all-xs",
  hideNavigationXs: "icon-hide-navigation-xs",
  showNavigationXs: "icon-show-navigation-xs",
  treeViewXs: "icon-tree-view-xs",
  menuXs: "icon-menu-xs",
  minimizeXs: "icon-minimize-xs",
  setupHub: "icon-protection-xs",
  star: "icon-star",
  deletedXs: "icon-deleted-xs",
  warningTriangleXs: "icon-warning-triangle-xs",
  mdmS: "icon-mdm-s",
  coroAiLite: "icon-coro-ai-lite",
  networkNoHexagonL: "icon-network-no-hex-l",
  coroMailNoHexagonL: "icon-coromail-2-no-hex-l",
  dnsS: "icon-dns-s",
  wifiS: "icon-wifi-s",
  infoFilled: "icon-info-solid-xs",
  ipRestriction: "icon-ip-restriction",
  backupAndRestoreL: "icon-backup-and-restore-l",
  backupAndRestoreS: "icon-backup-and-restore-s",
  restore: "icon-restore-s",
  backup: "icon-backup-s",
  suspiciousEmailContentS: "icon-suspicious-email-content-s",
};

const light: ThemeDefinition = {
  dark: false,
  colors: {
    primary: "#262260",
    error: "#c90000",
    white: "#FFFFFF",
    anchor: "#006eae",
    inactive: "#BBBBBB",
    link: "#006EAE",
    border: "#262260",
    "on-surface": "#262260",
    "on-white": "#262260",

    "green-base": "#46a762",
    "green-darkest": "#145229",
    "green-dark": "#0f7e34",
    "green-light": "#69d488",
    "green-faint": "#e6f4d8",
    "green-pale": "#EAF5E7",

    "indigo-darkest": "#100e2b",
    "indigo-dark": "#1f1d4a",
    "indigo-medium": "#6a6891",
    "indigo-light": "#b1b1e1",
    "indigo-pale": "#E2E2EB",
    "indigo-faint": "#EEEEF3",
    "on-indigo-faint": "#262260",
    "indigo-instructional": "#EDEDFF",
    "indigo-purple": "#5049A7",
    "indigo-sky": "#E4E4F9",

    "purple-faint": "#F5ECF9",

    "surface-variant": "#B1B1E1",

    "yellow-base": "#be6600",
    "yellow-light": "#f0aa59",
    "yellow-faint": "#f5efe5",
    "yellow-pale": "#FCF9DE",

    "orange-base": "#cf4b00",
    "orange-light": "#ec6b22",
    "orange-faint": "#fef1e1",

    "gray-dark": "#212121",
    "gray-neutral": "#606060",
    "gray-medium": "#bdbdbd",
    "gray-pale": "#ededed",
    "gray-faint": "#f9f9f9",
    "gray-light": "#e0e0e0",

    "red-base": "#f74b4b",
    "red-darkest": "#5e2222",
    "red-dark": "#c92626",
    "red-light": "#ff5c5c",
    "red-faint": "#f5e5e5",
    "red-pale": "#FAE8E6",

    "labels-green-full": "#7bc86c",
    "labels-green-light": "#b8ddb0",
    "labels-yellow-full": "#f5dd2a",
    "labels-yellow-light": "#f5ea92",
    "labels-orange-full": "#ffaf3f",
    "labels-orange-light": "#fbd19c",
    "labels-blue-full": "#28cce5",
    "labels-blue-light": "#8fdfeb",
    "labels-purple-light": "#dfc0eb",
    "labels-purple-full": "#cd8de5",
    "labels-red-full": "#ee7564",
    "labels-red-light": "#efb3ab",

    "blue-light": "#16A4F6",
    "blue-pale": "#DDF5F9",
  },
};

export default createVuetify({
  directives,
  locale: {
    // @ts-expect-error -- vuetify's i18n adapter expects a wider `I18n<any, ...>` shape than vue-i18n's strict generic; safe at runtime
    adapter: createVueI18nAdapter({ i18n, useI18n }),
  },
  display: {
    mobileBreakpoint: "sm",
    thresholds: {
      xs: 0,
      sm: 970,
      md: 1200,
      lg: 1600,
      xl: 1920,
    },
  },
  components: {
    ...labs,
    ...components,
  },
  icons: {
    defaultSet: "custom",
    aliases,
    sets: {
      custom,
    },
  },
  theme: {
    defaultTheme: "light",
    themes: {
      light,
    },
  },
  defaults: {
    VAutocomplete: {
      menuIcon: "icon-arrow-down-xs",
      // remove when this is fixed https://github.com/vuetifyjs/vuetify/issues/22416
      persistentPlaceholder: true,
    },
    VCombobox: {
      // remove when this is fixed https://github.com/vuetifyjs/vuetify/issues/22416
      persistentPlaceholder: true,
    },
    VTextField: {
      variant: "outlined",
      // remove when this is fixed https://github.com/vuetifyjs/vuetify/issues/22416
      persistentPlaceholder: true,
    },
    VCheckbox: {
      trueIcon: "icon-checkbox-marked",
      falseIcon: "icon-checkbox-blank-outline",
      indeterminateIcon: "icon-minus-box",
      color: "primary",
      density: "compact",
      hideDetails: true,
      ripple: false,
    },
    VCheckboxBtn: {
      trueIcon: "icon-checkbox-marked",
      falseIcon: "icon-checkbox-blank-outline",
      indeterminateIcon: "icon-minus-box",
      color: "primary",
      ripple: false,
    },
    VRadioGroup: {
      trueIcon: "icon-radiobox-marked",
      falseIcon: "icon-radiobox-blank",
      density: "compact",
    },
    VRadio: {
      density: "compact",
    },
    VCard: {
      elevation: 0,
      rounded: "lg",
    },
    VTab: {
      height: 32,
    },
    VTabs: {
      nextIcon: "$chevronRight",
      prevIcon: "$chevronLeft",
    },
    VBtn: {
      elevation: 0,
      variant: "flat",
    },
    VSelect: {
      variant: "outlined",
      menuIcon: "icon-arrow-down-xs",
      // remove when this is fixed https://github.com/vuetifyjs/vuetify/issues/22416
      persistentPlaceholder: true,
      clearIcon: "icon-rules-close",
    },
    VDataTable: {
      itemsPerPageOptions: paginationOptions,
    },
    VDataTableServer: {
      rounded: "circle",
      activeColor: "primary",
      color: "primary",
      prevIcon: "$chevronLeft",
      nextIcon: "$chevronRight",
      firstIcon: "$firstPage",
      lastIcon: "$lastPage",
      itemsPerPageOptions: paginationOptions,
    },
    VDataTableFooter: {
      prevIcon: "$chevronLeft",
      nextIcon: "$chevronRight",
      firstIcon: "$firstPage",
      lastIcon: "$lastPage",
    },
    VPagination: {
      rounded: "circle",
      activeColor: "primary",
      color: "primary",
      prevIcon: "$chevronLeft",
      nextIcon: "$chevronRight",
      firstIcon: "$firstPage",
      lastIcon: "$lastPage",
    },
  },
});
